/**
 * pipelineSnippets — pure functions that build the YAML / curl /
 * GitLab CI snippet a customer pastes into their pipeline to invoke
 * the Pipeline Integration endpoint (#1191, Slice 2 of meta #1189).
 *
 * **Why a pure function module.** The drawer's snippet block updates
 * live on every keystroke / radio click / mode-tab switch. Output is a
 * stable function of `{templateId, workspaceId, urlStrategy, mode,
 * variantBaseUrlForFallback}` — no async, no I/O. That makes the body
 * straightforward to unit test with string-equality assertions and
 * keeps the React layer free of template logic.
 *
 * **Mode enum is vendor-neutral.** Mode tabs render different OUTPUT
 * for the same underlying call — they don't represent different scope.
 * Per the architectural rails on #1189, the enum is generic
 * (`PipelineSnippetMode`) and the members are named after what the
 * output FORMAT is, not which vendor consumes it. Adding more CI
 * modes later is incremental — one helper per mode, one tab per
 * member.
 *
 * **GitHub Actions mode.** The GitHub Actions output is a `run:` step
 * that invokes curl against the pipeline API.
 *
 * **Token secret name is fixed.** All three snippets reference
 * `$OTTOTESTER_API_TOKEN` so the in-drawer callout's "store as
 * OTTOTESTER_API_TOKEN" instruction matches the snippet the operator
 * is about to paste.
 *
 * **Snake_case in the wire body.** The Pipeline Integration endpoint
 * (`/api/v1/pipelines/runs`, shipped #1190) takes snake_case — that's
 * the customer-pasted contract. The translation to camelCase happens
 * server-side in the route handler.
 */

/** Discriminator for which output format the snippet block renders.
 * "GitHub Actions" / "curl" / "GitLab CI" in the UI; the enum members
 * are vendor-neutral capability names rather than vendor names so
 * adding new CI integrations later (Jenkins, CircleCI, Drone) is an
 * additive change to the enum without re-naming the existing
 * members. */
export type PipelineSnippetMode = 'githubActions' | 'curl' | 'gitlabCi'

/** Discriminator for how the snippet gets the target URL. The three
 * customer-meaningful patterns the mockup spec'd:
 *   - `envVar`: snippet expands `$DEPLOY_URL` (the customer's
 *     pipeline sets it before invoking).
 *   - `hardcoded`: snippet literal-strings a value the customer types
 *     into the drawer's URL input.
 *   - `variantDefault`: snippet OMITS `target_url`; the backend
 *     falls back to the variant's configured `baseUrl`. */
export type PipelineUrlStrategy = 'envVar' | 'hardcoded' | 'variantDefault'

/** The public API origin the snippet calls. Falls back to the page
 * origin during dev / local — the prod URL is the runtime origin in
 * production builds so we don't need a build-time constant. The
 * caller passes this in so the function stays pure (no
 * `window.location` reads inside). */
export interface BuildPipelineSnippetInput {
  mode: PipelineSnippetMode
  urlStrategy: PipelineUrlStrategy
  /** Workspace UUID — drops into the `workspace_id` body field. */
  workspaceId: string
  /** RunTemplate id — drops into the `template_id` body field. Empty
   * string when the operator hasn't picked one yet; the snippet
   * renders a `<select-a-suite>` placeholder so the operator sees
   * the shape even pre-selection. */
  templateId: string
  /** Operator-typed URL when `urlStrategy === 'hardcoded'`. Ignored
   * for the other two strategies. Empty string renders as a
   * placeholder so the snippet doesn't break visually. */
  hardcodedUrl: string
  /** The public API origin to call. Production: app.ottotester.ai;
   * dev: the page origin. */
  apiOrigin: string
}

/** Filename suggestion for the snippet's header — purely cosmetic, but
 * matches the convention developers expect (the file they'd save the
 * snippet to). */
export function snippetFilenameFor(mode: PipelineSnippetMode): string {
  switch (mode) {
    case 'githubActions':
      return '.github/workflows/ottotester.yml'
    case 'curl':
      return 'run-ottotester.sh'
    case 'gitlabCi':
      return '.gitlab-ci.yml'
  }
}

/** Customer-facing label for each mode tab. PascalCase / proper noun
 * capitalization per the brand vocabulary rule — these are the strings
 * the operator reads. */
export function snippetModeLabel(mode: PipelineSnippetMode): string {
  switch (mode) {
    case 'githubActions':
      return 'GitHub Actions'
    case 'curl':
      return 'curl'
    case 'gitlabCi':
      return 'GitLab CI'
  }
}

/** Render-once placeholder when the operator hasn't picked a template
 * yet. Snippet still renders with the shape — the unfilled slot signals
 * "you still need to pick a suite." */
const TEMPLATE_PLACEHOLDER = '<select-a-suite>'

/** Render-once placeholder for the hardcoded-URL mode when the
 * operator hasn't typed a value yet. */
const URL_PLACEHOLDER = 'https://staging.example.com'

/** Builds the body parts used in every mode. The snippet helpers
 * below stitch these into the per-mode skeleton.
 *
 * **Shell-quoting subtlety (Copilot review on #1191).** The curl /
 * GitLab CI / GitHub Actions snippets wrap the JSON body in
 * single-quotes (`-d '{...}'`). Inside single quotes the shell does
 * NOT expand `$VAR` — so for the envVar URL strategy a naive
 * `"target_url": "$DEPLOY_URL"` ships the literal string `$DEPLOY_URL`
 * to the backend instead of the runtime value. The fix is the
 * standard bash quote-break: close the single quotes, splice the
 * variable in double quotes, reopen the single quotes. The JSON
 * sees `... "target_url": "<expanded-value>" ...`; the shell sees
 * three concatenated argv chunks.
 *
 * The Authorization header is unaffected because it lives in its
 * OWN `-H "..."` argument with double quotes — `$OTTOTESTER_API_TOKEN`
 * expands inside double quotes the normal way. The body is the
 * special case because we want JSON to be the dominant grammar
 * (single-quoted, no shell metachar surprises) with a controlled
 * expansion hole for the env-var URL pattern. */
interface SnippetBodyParts {
  /** Comment block describing what the snippet does — surfaces above
   * the call in curl / GitLab modes. */
  comment: string
  /** Whether the `target_url` field gets emitted at all (false for
   * variantDefault — backend falls back to the variant's URL). */
  emitTargetUrl: boolean
  /** The exact bytes for the `target_url` field's VALUE side, ready
   * to be spliced between a leading `"target_url": ` and a trailing
   * comma. Already shell-escaped for the surrounding single-quoted
   * JSON: envVar strategy returns the quote-break sequence; hardcoded
   * returns a plain quoted literal. */
  targetUrlJsonFragment: string
}

function buildBodyParts(input: BuildPipelineSnippetInput): SnippetBodyParts {
  switch (input.urlStrategy) {
    case 'envVar':
      // Shell quote-break: `'... "target_url": "'"$DEPLOY_URL"'", ...'`.
      // Close the outer single-quoted JSON, splice the variable
      // inside double quotes (where the shell expands it), reopen
      // the single-quoted JSON. The JSON reader sees a clean string
      // value bounded by literal `"` on either side.
      return {
        comment:
          'Runs the suite against the URL your pipeline sets in DEPLOY_URL.',
        emitTargetUrl: true,
        targetUrlJsonFragment: `"\'"$DEPLOY_URL"\'"`,
      }
    case 'hardcoded':
      return {
        comment: 'Runs the suite against the URL hardcoded below.',
        emitTargetUrl: true,
        targetUrlJsonFragment: `"${input.hardcodedUrl || URL_PLACEHOLDER}"`,
      }
    case 'variantDefault':
      return {
        // Plain-English explanation that the variant's configured URL
        // is used. Avoids surfacing `baseUrl` (code identifier) here —
        // customer-facing copy uses "configured URL."
        comment: "Runs the suite against this variant's configured URL.",
        emitTargetUrl: false,
        targetUrlJsonFragment: '',
      }
  }
}

/** Shared "what URL the snippet targets" line that the route on the
 * server expects. The `/v1/pipelines/runs` path is locked in #1190. */
function pipelineRunsUrl(apiOrigin: string): string {
  return `${apiOrigin.replace(/\/$/, '')}/api/v1/pipelines/runs`
}

/** GitHub Actions YAML — a `run:` step invoking curl against the
 * pipeline API. */
function buildGitHubActionsSnippet(input: BuildPipelineSnippetInput): string {
  const parts = buildBodyParts(input)
  const templateId = input.templateId || TEMPLATE_PLACEHOLDER
  // Inline the JSON body inside the shell heredoc-free single-line
  // form `'{...}'` because it's the form that survives the YAML
  // |-block indentation gracefully. The body's snake_case keys match
  // the wire contract. `targetUrlJsonFragment` already carries the
  // shell quote-break sequence for envVar mode (see SnippetBodyParts
  // docstring) so it's spliced in raw rather than wrapped in quotes
  // here.
  const bodyFields = [
    `"workspace_id": "${input.workspaceId}"`,
    `"template_id": "${templateId}"`,
  ]
  if (parts.emitTargetUrl) {
    bodyFields.push(`"target_url": ${parts.targetUrlJsonFragment}`)
  }
  bodyFields.push('"wait_for_completion": true')
  bodyFields.push('"fail_on_test_failure": true')
  const body = `{ ${bodyFields.join(', ')} }`

  return `# ${parts.comment}
name: OttoTester

on:
  workflow_dispatch:
  # Add deployment_status / push triggers as your pipeline needs them.

jobs:
  run-suite:
    runs-on: ubuntu-latest
    steps:
      - name: Run OttoTester suite
        env:
          OTTOTESTER_API_TOKEN: \${{ secrets.OTTOTESTER_API_TOKEN }}
        run: |
          curl -X POST ${pipelineRunsUrl(input.apiOrigin)} \\
            -H "Authorization: Bearer $OTTOTESTER_API_TOKEN" \\
            -H "Content-Type: application/json" \\
            -d '${body}'
`
}

/** curl — the lowest-common-denominator form. Works in Jenkins,
 * CircleCI, Drone, ad-hoc shell, anywhere with bash + curl. */
function buildCurlSnippet(input: BuildPipelineSnippetInput): string {
  const parts = buildBodyParts(input)
  const templateId = input.templateId || TEMPLATE_PLACEHOLDER
  const bodyLines: string[] = []
  bodyLines.push(`    "workspace_id": "${input.workspaceId}",`)
  bodyLines.push(`    "template_id": "${templateId}",`)
  if (parts.emitTargetUrl) {
    // See SnippetBodyParts: `targetUrlJsonFragment` already includes
    // shell quote-break for envVar mode so $DEPLOY_URL expands.
    bodyLines.push(`    "target_url": ${parts.targetUrlJsonFragment},`)
  }
  bodyLines.push('    "wait_for_completion": true,')
  bodyLines.push('    "fail_on_test_failure": true')
  return `# ${parts.comment}
# Set OTTOTESTER_API_TOKEN in your CI secrets before invoking.

curl -X POST ${pipelineRunsUrl(input.apiOrigin)} \\
  -H "Authorization: Bearer $OTTOTESTER_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
${bodyLines.join('\n')}
  }'
`
}

/** GitLab CI — the `.gitlab-ci.yml` shape. Single `script:` block
 * invoking curl identical to the curl-mode payload. Adding more
 * stages / dependencies is the customer's job; we just provide the
 * one-job skeleton. */
function buildGitLabCiSnippet(input: BuildPipelineSnippetInput): string {
  const parts = buildBodyParts(input)
  const templateId = input.templateId || TEMPLATE_PLACEHOLDER
  const bodyFields = [
    `"workspace_id": "${input.workspaceId}"`,
    `"template_id": "${templateId}"`,
  ]
  if (parts.emitTargetUrl) {
    // See SnippetBodyParts: `targetUrlJsonFragment` already includes
    // shell quote-break for envVar mode so $DEPLOY_URL expands.
    bodyFields.push(`"target_url": ${parts.targetUrlJsonFragment}`)
  }
  bodyFields.push('"wait_for_completion": true')
  bodyFields.push('"fail_on_test_failure": true')
  const body = `{ ${bodyFields.join(', ')} }`

  return `# ${parts.comment}
# Set OTTOTESTER_API_TOKEN in GitLab CI/CD variables before invoking.

ottotester:
  stage: test
  image: curlimages/curl:latest
  script:
    - |
      curl -X POST ${pipelineRunsUrl(input.apiOrigin)} \\
        -H "Authorization: Bearer $OTTOTESTER_API_TOKEN" \\
        -H "Content-Type: application/json" \\
        -d '${body}'
`
}

/** Build a snippet string for the chosen mode + URL strategy. Pure
 * function — same inputs → same output. The drawer calls this on
 * every state change to refresh the visible code block. */
export function buildPipelineSnippet(input: BuildPipelineSnippetInput): string {
  switch (input.mode) {
    case 'githubActions':
      return buildGitHubActionsSnippet(input)
    case 'curl':
      return buildCurlSnippet(input)
    case 'gitlabCi':
      return buildGitLabCiSnippet(input)
  }
}
